import React from 'react';
import { motion } from 'framer-motion';
import { TOKENS, WA_LINK } from '../tokens';
import { ChapterTag } from './Details';

function Ring({ size, delay, opacity }) {
  return (
    <div style={{
      position: 'absolute',
      width: size, height: size,
      borderRadius: '50%',
      border: `1px solid rgba(200,169,106,${opacity})`,
      animation: `ringBreathe 6s ease-in-out ${delay}s infinite`,
    }} />
  );
}

export default function Price() {
  return (
    <section id="the-price" style={{
      background: '#050A1A',
      minHeight: '100vh',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      position: 'relative', overflow: 'hidden',
    }}>
      <style>{`
        @keyframes ringBreathe {
          0%,100% { transform:scale(0.97); opacity:0.5; }
          50%      { transform:scale(1.03); opacity:1; }
        }
        @keyframes priceGlow {
          0%,100% { text-shadow: 0 0 60px rgba(200,169,106,0); }
          50%      { text-shadow: 0 0 60px rgba(200,169,106,0.15); }
        }
      `}</style>

      {/* Rings */}
      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
        <Ring size={300} delay={0}   opacity={0.06} />
        <Ring size={520} delay={1}   opacity={0.05} />
        <Ring size={740} delay={2}   opacity={0.04} />
        <Ring size={960} delay={3}   opacity={0.025} />
      </div>

      {/* Content */}
      <div style={{ position: 'relative', zIndex: 2, textAlign: 'center', padding: '0 24px' }}>
        <ChapterTag text="Chapter 05 · The Number" />

        <motion.span
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          style={{
            display: 'block',
            fontSize: '0.66rem', fontWeight: 500,
            letterSpacing: '0.25em', textTransform: 'uppercase',
            color: TOKENS.GOLD, marginBottom: 32,
          }}
        >
          Asking Price
        </motion.span>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 'clamp(5rem, 14vw, 12rem)',
            fontWeight: 300, lineHeight: 1,
            letterSpacing: '-0.02em',
            color: '#fff',
            display: 'inline-block',
            animation: 'priceGlow 4s ease-in-out infinite',
          }}
        >
          <span style={{ fontSize: '0.5em', verticalAlign: 'top', marginTop: '0.15em', color: TOKENS.GOLD, marginRight: '-0.03em', display: 'inline-block' }}>₹</span>
          5.55
          <span style={{ fontSize: '0.5em', verticalAlign: 'bottom', marginBottom: '0.1em', color: 'rgba(255,255,255,0.55)', display: 'inline-block' }}> Cr</span>
        </motion.div>

        <motion.span
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, delay: 0.4 }}
          style={{
            display: 'block',
            fontSize: '0.7rem', fontWeight: 400,
            letterSpacing: '0.18em', textTransform: 'uppercase',
            color: TOKENS.MUTED, marginTop: 32,
          }}
        >
          Negotiable
        </motion.span>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.55 }}
          style={{ display: 'flex', gap: 14, justifyContent: 'center', marginTop: 56, flexWrap: 'wrap' }}
        >
          <a
            href="#contact"
            onClick={e => { e.preventDefault(); document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
            style={btnGold}
            onMouseEnter={e => { e.currentTarget.style.background = TOKENS.GOLD_LIGHT; e.currentTarget.style.transform = 'scale(1.04)'; e.currentTarget.style.boxShadow = '0 0 40px rgba(200,169,106,0.3)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = TOKENS.GOLD; e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = 'none'; }}
          >
            Schedule Site Visit
          </a>
          <a
            href={WA_LINK()}
            target="_blank" rel="noreferrer"
            style={btnWa}
            onMouseEnter={e => { e.currentTarget.style.background = '#1db954'; e.currentTarget.style.transform = 'scale(1.04)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#25D366'; e.currentTarget.style.transform = 'scale(1)'; }}
          >
            <WaIcon /> WhatsApp Now
          </a>
        </motion.div>
      </div>
    </section>
  );
}

function WaIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  );
}

const btnGold = {
  display: 'inline-flex', alignItems: 'center', gap: 8,
  padding: '14px 28px', background: '#C8A96A', color: '#000',
  fontFamily: "'Outfit', sans-serif", fontSize: '0.78rem', fontWeight: 500,
  letterSpacing: '0.04em', borderRadius: 980, textDecoration: 'none',
  transition: 'all 0.3s cubic-bezier(0.16,1,0.3,1)', cursor: 'none',
};

const btnWa = {
  display: 'inline-flex', alignItems: 'center', gap: 10,
  padding: '14px 28px', background: '#25D366', color: '#fff',
  fontFamily: "'Outfit', sans-serif", fontSize: '0.78rem', fontWeight: 500,
  letterSpacing: '0.04em', borderRadius: 980, textDecoration: 'none',
  transition: 'all 0.3s', cursor: 'none',
};
